<template>
    <div class="d-inline">
        <b-button :variant="btnVariant" @click="open">{{btnText}}</b-button>
        <b-modal ref="instagram-modal" hide-footer :title="title">
            <div class="text-center">
                <template v-if="step === 1">
                    <ul class="account-list">
                        <li v-for="account in accounts" v-bind:key="account.id">
                            <span class="account">@{{account.name}}</span>

                            <b-button v-if="account.instagramPermissions" class="choose" variant="primary" size="sm" @click="select(account)">Choose</b-button>
                            <b-button v-else class="choose" variant="secondary" size="sm" @click="setOpen(true)" href="https://ritekit.com/accounts/?do=instagramLogin-login">Reconnect</b-button>

                            <span v-if="!account.instagramPermissions" class="text-danger float-right mr-2">Not enough permissions</span>
                        </li>
                    </ul>
                    <p>
                        <b-button class="facebook-button" @click="setOpen(true)" href="https://ritekit.com/accounts/?do=instagramLogin-login"><font-awesome-icon :icon="['fab', 'facebook-f']"/> Connect Instagram Business Profile using Facebook</b-button>
                    </p>
                    <p class="text-center">
                        <em>Note: Instagram allows us to analyze only Instagram Business Profiles. Every Instagram Business profile has to be connected to a Facebook page, which is why we need you to authenticate through Facebook.</em>
                    </p>
                </template>
                <template v-else-if="step === 2">
                    <p class="price text-success">
                        <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="fa-spin text-muted"/>
                        <span v-else-if="posts >= 30">${{price}}</span>
                        <span class="text-muted" v-else>Not enough data</span>
                    </p>
                    <p>
                        RiteTag Coach will analyze
                        <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="fa-spin text-muted"/>
                        <strong v-else class="posts text-success">{{posts}}</strong>
                        posts and
                        <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="fa-spin text-muted"/>
                        <strong v-else class="comments text-success">{{comments}}</strong>
                        comments
                        <br>from <strong class="text-success">{{startDate | formatDate}} - {{endDate | formatDate}}</strong> &nbsp;&nbsp;&nbsp;<a href="#" @click="changeDate">Change dates</a>
                    </p>

                    <p>
                        <b-button variant="primary" :disabled="posts < 30" size="lg" @click="purchase">Purchase report <span v-if="price > 0">for ${{price}}</span></b-button>
                    </p>

                </template>
                <template v-else-if="step === 3">
                    <p>
                    <datetime
                            class="date-control theme-ritekit"
                            input-class="form-control"
                            v-model="startDate"
                            format="yyyy-MM-dd"
                            :min-datetime="minDate"
                            :max-datetime="endDate"
                    />
                    &nbsp; - &nbsp;
                    <datetime
                            class="date-control theme-ritekit"
                            input-class="form-control"
                            v-model="endDate"
                            format="yyyy-MM-dd"
                            :min-datetime="startDate"
                            :max-datetime="maxDate"
                    />
                    </p>
                    <p>
                    <b-button variant="primary" @click="calculate">Calculate price</b-button>
                    </p>
                    <p>
                        <b-alert :show="postsNotice" variant="warning">
                            Not enough posts were sent during selected dates.<br>
                            Please adjust the dates.
                        </b-alert>
                    </p>
                </template>
                <template v-else-if="step === 4">
                    <Payment :price="price" :query="query" :callback="complete"/>
                </template>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import moment from 'moment'
    import {DateTime} from 'luxon'
    import {mapState} from 'vuex'
    import Payment from '../../common/Payment'
    import {api} from '../../_services/ritetag'

    export default {
        name: 'InstagramModal',
        components: {
            Payment
        },
        props: {
            btnVariant: {
                default: 'primary',
                type: String
            },
            btnText: {
                default: 'Create new report',
                type: String
            }
        },
        data () {
            return {
                step: 1,
                selectedInstagramAccount: 0,
                price: 0,
                posts: 0,
                comments: 0,
                startDate: moment().subtract(12, 'month').format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD'),
                loading: false
            }
        },
        computed: {
            ...mapState('account', [
                'info'
            ]),
            postsNotice () {
                return this.posts < 30
            },
            accounts () {
                const def = []
                return def.concat(this.info.accounts.instagram)
            },
            title () {
                let title = 'Create Instagram Account Report'
                return this.selectedInstagramAccount ? title + ' for @' + this.selectedInstagramAccount.name : 'Choose Instagram Account'
            },
            maxDate () {
                return DateTime.utc().toSQLDate()
            },
            minDate () {
                return moment().subtract(12, 'month').format('YYYY-MM-DD')
            },
            query () {
                return {
                    accountName: this.selectedInstagramAccount.name,
                    accountId: this.selectedInstagramAccount.id,
                    from: this.startDate,
                    to: this.endDate
                }
            }
        },
        methods: {
            complete() {
                this.$refs['instagram-modal'].hide()
                this.$emit('created')
                this.$notify({
                    text: 'Report successfully created'
                })
            },
            open () {
                this.step = 1
                this.$refs['instagram-modal'].show()
            },
            select (selected) {
                this.selectedInstagramAccount = selected
                this.calculate()
                this.step = 2
            },
            changeDate () {
                this.step = 3
            },
            calculate () {
                this.step = 2
                this.loading = true
                api.getInstagramCalc(this.selectedInstagramAccount.id, this.startDate, this.endDate)
                    .then(res => {
                        this.posts = res.data.posts
                        this.comments = res.data.comments
                        this.price = res.data.price
                        this.loading = false
                    })
                // this.posts = Math.floor(Math.random() * 100)
                // this.comments = Math.floor(Math.random() * 1000)
            },
            purchase () {
                this.step = 4
            },
            setOpen (state) {
                localStorage.setItem('instagram-report-modal', JSON.stringify(state))
            }
        },
        watch: {
            step (s) {
                switch (s) {
                case 1:
                    this.selectedInstagramAccount = null
                    break;
                }
            }
        },
        mounted () {
            const open = JSON.parse(localStorage.getItem('instagram-report-modal'))
            if (open) {
                this.setOpen(false)
                this.open()
            }
        }
    }
</script>

<style lang="scss" scoped>
    .account-list {
        padding: 10px 20px;
        text-align: left;
        list-style: none;

        > li {
            height: 30px;
        }

        .choose {
            float: right;
        }
    }

    .price {
        font-size: 52px;
    }

    .date-control {
        width: 200px;
        display: inline-block;
    }

    .facebook-button {
        background-color: #3C5A99;
        color:white;

        &:hover {
            background-color: #4d6cad;
            color: white;
        }
    }

    .theme-ritekit .vdatetime-popup__header,
    .theme-ritekit .vdatetime-calendar__month__day--selected > span > span,
    .theme-ritekit .vdatetime-calendar__month__day--selected:hover > span > span {
        background: #c12f93;
    }

    .theme-ritekit .vdatetime-year-picker__item--selected,
    .theme-ritekit .vdatetime-time-picker__item--selected,
    .theme-ritekit .vdatetime-popup__actions__button {
        color: #c12f93;
    }
</style>
